import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { singleInstantSearch } from "@/features/instantsearch/services/single-instantsearch";
import type { TypesenseProductDocument } from "@/features/instantsearch/types/instantsearch-types";
import { useOrderGroup } from "@/api/hooks/use-orders";
import useUIPreferencesStore from "@/store/use-ui-preferences-store";
import useAuthStore from "@/store/use-auth-store";

/**
 * Hook to fetch product recommendations based on order items
 *
 * Strategy:
 * - Extract all productId's from order items
 * - Exclude order productId's (client-side filter)
 * - Get diverse products from ALL categories (no categoryId filter)
 * - Group by categoryId - 2-3 products per category
 * - Random sorting for variety
 * - Return max 20 products
 */
export const useOrderRecommendations = () => {
  const { orderGroupId } = useParams<{ orderGroupId: string }>();
  const language = useUIPreferencesStore((state) => state.language);
  const region = useAuthStore((state) => state.user?.region || "");

  // Fetch order group data
  const {
    data: orderGroupResponse,
    isLoading: isOrderLoading,
  } = useOrderGroup(orderGroupId || null);

  // Extract orders from response
  const orders = orderGroupResponse?.data?.orders || [];

  // Extract all productId's from order items
  const orderProductIds = orders.reduce<string[]>((acc, order) => {
    const productIds = order.items?.map((item) => item.productId).filter(Boolean) || [];
    return [...acc, ...productIds];
  }, []);

  // 🎲 Her 10 dakikada bir değişen seed - farklı ürünler için
  const seed = Math.floor(Date.now() / (10 * 60 * 1000)); // 10 dakikalık window

  // Query recommendations
  const {
    data: recommendationsData,
    isLoading: isRecommendationsLoading,
    error: recommendationsError,
  } = useQuery({
    queryKey: ["order-recommendations", orderGroupId, orderProductIds, seed, language, region],
    queryFn: async () => {
      // Order data yoksa boş döndür
      if (!orderGroupId || orders.length === 0 || orderProductIds.length === 0) {
        return [];
      }

      try {
        // 🔍 Debug: Query başlangıç
        const pageNumber = (seed % 3) + 1; // 1-3 arası sayfa numarası
        console.log("🎯 useOrderRecommendations - Query Start:", {
          orderGroupId,
          orderProductIdsCount: orderProductIds.length,
          orderProductIds: orderProductIds.slice(0, 5), // İlk 5'i göster
          seed,
          pageNumber,
        });

        // Multi-search ile recommendations çek
        // Strategy:
        // - TÜM kategorilerden çeşitli ürünler (categoryId filtresi YOK)
        // - Her kategoriden 2-3 ürün (categoryId group by)
        // - Seed-based page number - Her 10 dakikada farklı ürünler
        // - Sadece order'daki productId'leri exclude et (client-side filter)
        // - Client-side shuffle ile çeşitlilik sağlanıyor
        
        const result = await singleInstantSearch<TypesenseProductDocument>({
          query: "*",
          // categoryId: undefined, // Tüm kategorilerden
          groupBy: "categoryId,familyId", // 🆕 Category ve family bazlı gruplama
          groupLimit: 3, // 🆕 Her category'den 3 ürün
          page: pageNumber, // 🎲 Seed-based page (1-3 arası)
          perPage: 30, // 🆕 Daha fazla çek (client-side filter sonrası 20'ye düşecek)
          // sortBy kaldırıldı - default effectivePrice:desc kullanılacak
          language,
          region,
        });

        // 🔍 Debug: Before filter
        console.log("🔍 Before Filter:", {
          totalHits: result.hits.length,
          orderProductIdsCount: orderProductIds.length,
        });

        // Client-side filter: Order'daki productId'leri exclude et
        const filteredResults = result.hits.filter((hit) => {
          const hitId = hit.productId || hit.id || "";
          // Order'daki productId'lerde yoksa dahil et
          return !orderProductIds.includes(hitId);
        });

        // 🔍 Debug: After filter
        console.log("🔍 After Filter:", {
          beforeFilter: result.hits.length,
          afterFilter: filteredResults.length,
          orderProductIdsCount: orderProductIds.length,
        });

        // 🎲 Client-side random shuffle - Daha fazla çeşitlilik için
        const shuffledResults = filteredResults.sort(() => Math.random() - 0.5);

        // 🔍 Debug: Final results
        console.log("✅ Final Results:", {
          afterShuffle: shuffledResults.length,
          finalCount: shuffledResults.slice(0, 20).length,
        });

        // Sıralama ve limit - Max 20 ürün
        return shuffledResults.slice(0, 20);
      } catch (errorResponse: any) {
        console.error("❌ Error fetching order recommendations:", errorResponse);
        return [];
      }
    },
    enabled: !isOrderLoading && !!orderGroupId && orderProductIds.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });

  // 🔍 Debug: Hook return değerleri
  console.log("📦 Hook Return:", {
    productsCount: recommendationsData?.length || 0,
    isLoading: isRecommendationsLoading || isOrderLoading,
    hasError: !!recommendationsError,
  });

  return {
    recommendations: recommendationsData || [],
    isLoading: isRecommendationsLoading || isOrderLoading,
    error: recommendationsError,
  };
};

